import { useId } from "react";

import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  useListItem,
} from "@floating-ui/react";

import {
  MENU_ITEM_BUTTON_CLASS,
  MenuItemContent,
  MenuSeparator,
  MenuSectionTitle,
} from "@/components/sidebar/MenuItemContent";
import { twMerge } from "@/utils/twMerge";

import type { ContextMenuApi } from "./useContextMenu";
import type {
  DropdownComponentItem,
  DropdownItem,
  DropdownItemRenderProps,
} from "@/components/sidebar/Dropdown";
import type { ComponentProps, KeyboardEvent, MouseEvent, ReactNode } from "react";

/**
 * Items the floating context menu accepts. Unlike the headlessui `Dropdown`, it does not
 * support the static `component` variant — custom rows must use `renderItem` so they can join
 * keyboard navigation (receive the item props/ref) instead of rendering as an inert node.
 */
export type ContextMenuItem = Exclude<DropdownItem, DropdownComponentItem>;

export function ContextMenuPanel({
  menu,
  items,
  className,
}: {
  menu: ContextMenuApi;
  items: ContextMenuItem[];
  className?: string;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === "Enter" || event.key === " ") && menu.activeIndex != null) {
      const activeItem = menu.listRef.current[menu.activeIndex];

      if (activeItem) {
        event.preventDefault();
        activeItem.click();
      }
    }
  };

  return (
    <FloatingPortal>
      {menu.open && (
        <FloatingFocusManager
          context={menu.context}
          modal={false}
          initialFocus={menu.refs.floating}
        >
          <div
            ref={menu.refs.setFloating}
            style={menu.floatingStyles}
            tabIndex={-1}
            className={twMerge(
              "min-w-[250px] p-1.5 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted z-100",
              "no-scrollbar outline-none overflow-y-auto",
              className,
            )}
            {...menu.getFloatingProps({ onKeyDown: handleKeyDown })}
          >
            <FloatingList elementsRef={menu.listRef} labelsRef={menu.labelsRef}>
              {items.map((item, index) => {
                if ("type" in item) {
                  if (item.type === "separator") {
                    const key = `separator-${index}`;
                    return <MenuSeparator key={key} />;
                  }

                  if (item.type === "title") {
                    const key = `title-${index}`;
                    return <MenuSectionTitle key={key} title={item.title} />;
                  }

                  return null;
                }

                if ("renderItem" in item) {
                  const key = `render-${index}`;
                  return <ContextMenuRenderItem key={key} menu={menu} render={item.renderItem} />;
                }

                const key = `${item.title}-${index}`;

                return (
                  <ContextMenuItem
                    key={key}
                    menu={menu}
                    data-testid={item.testId}
                    aria-label={item.title}
                    disabled={item.disabled}
                    className={twMerge(
                      MENU_ITEM_BUTTON_CLASS,
                      "outline-none",
                      !item.disabled &&
                        "hover:bg-sidebar-button-active-bg data-[focus]:bg-sidebar-button-active-bg",
                      item.disabled && "opacity-50",
                      item.className,
                    )}
                    onSelect={
                      item.disabled
                        ? undefined
                        : (event) => item.onClick?.(event as MouseEvent<HTMLButtonElement>)
                    }
                  >
                    <MenuItemContent item={item} />
                  </ContextMenuItem>
                );
              })}
            </FloatingList>
          </div>
        </FloatingFocusManager>
      )}
    </FloatingPortal>
  );
}

function ContextMenuItem({
  menu,
  onSelect,
  children,
  ...rest
}: ComponentProps<"button"> & {
  menu: ContextMenuApi;
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
}) {
  const itemProps = useContextMenuItemProps(menu, onSelect);

  return (
    <button type="button" {...itemProps} {...rest}>
      {children}
    </button>
  );
}

function ContextMenuRenderItem({
  menu,
  render,
}: {
  menu: ContextMenuApi;
  render: (props: DropdownItemRenderProps) => ReactNode;
}) {
  const itemProps = useContextMenuItemProps(menu);
  return <>{render(itemProps)}</>;
}

function useContextMenuItemProps(
  menu: ContextMenuApi,
  onSelect?: (event: MouseEvent<HTMLElement>) => void,
): DropdownItemRenderProps {
  const id = useId();
  const { ref, index } = useListItem();
  const isActive = menu.activeIndex === index;

  return {
    ...menu.getItemProps({
      onClick(event: MouseEvent<HTMLElement>) {
        onSelect?.(event);

        if (event.defaultPrevented) {
          return;
        }

        menu.close();
      },
    }),
    ref,
    role: "menuitem",
    id,
    tabIndex: -1,
    "data-focus": isActive ? "" : undefined,
  } as DropdownItemRenderProps;
}
